/**
 * Кассын «Худалдан авагч» хэсэг — бүгд заавал биш.
 *
 * Регистр ЗӨВХӨН хувь зээлд (`required`) — бусад үед оронд нь «Урамшуулал»
 * (`PromotionField`, 2026-11-03).
 *
 * ⚠️ Утас, регистрийг ЭНД нормчилно (`@boots/domain`) — сервер рүү
 *    нормчилсон утга л явна (`+976XXXXXXXX`, DB `CHECK`-тэй).
 *
 * ⚠️ Оруулсан атлаа буруу бол `null` буцаана: чимээгүй алгасвал
 *    баталгаа хэнд ч холбогдохгүй үлдэнэ. Эцэг хуудас «Зарах» товчийг
 *    хаана.
 *
 * Утас зөв бол бүртгэлтэй эсэхийг хайна (`GET /customers/by-phone`,
 * `customers.view` эрхтэй үед) — олдвол ХООСОН нэр, регистрийг бөглөж
 * «✓ Бүртгэлтэй худалдан авагч» гэж хэлнэ (dark glass дизайн).
 * Кассчны шивсэн утгыг хэзээ ч дарж бичихгүй.
 */

import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatQty, normalizePhone, normalizeRegistryNo } from '@boots/domain';
import { ApiError, api, type Envelope } from '../lib/api';
import { useAuth } from '../lib/auth';
import { PromotionField } from './promotion-field';
import { Card, CardTitle, Field, Input } from './ui';

interface KnownCustomer {
  name: string | null;
  registryNo: string | null;
  _count: { orders: number };
}

export interface CustomerInput {
  customerPhone?: string;
  customerName?: string;
  customerRegistryNo?: string;
  marketingConsent: boolean;
  /** Өгсөн урамшууллын нэр — сервер `lookups(PROMOTION)`-д олно/үүсгэнэ */
  promotion?: string;
}

export function CustomerFields({
  onChange,
  required = false,
}: {
  /** Хүчинтэй бол утгууд, оруулсан утга буруу бол `null`. */
  onChange: (customer: CustomerInput | null) => void;
  /** Хувь зээл — утас, регистр ХОЁУЛАА заавал (сервер ч шаардана). */
  required?: boolean;
}) {
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [registry, setRegistry] = useState('');
  const [consent, setConsent] = useState(false);
  const [promotion, setPromotion] = useState('');

  const phoneOk = phone.trim() ? normalizePhone(phone) : null;
  // Регистр зөвхөн хувь зээлд харагдана — нуугдсан үед (автоматаар бөглөгдсөн
  // ч) илгээхгүй, шалгахгүй.
  const registryOk = required && registry.trim() ? normalizeRegistryNo(registry) : null;
  const phoneBad = phone.trim() !== '' && !phoneOk;
  const registryBad = required && registry.trim() !== '' && !registryOk;

  const missing = required && (!phoneOk || !registryOk);

  const { hasPerm } = useAuth();
  const canLookup = hasPerm('customers.view');
  const lookup = useQuery({
    queryKey: ['customer-by-phone', phoneOk],
    queryFn: () => api<Envelope<KnownCustomer>>(`/customers/by-phone/${encodeURIComponent(phoneOk!)}`),
    enabled: !!phoneOk && canLookup,
    retry: false,
    staleTime: 60_000,
  });
  const known = phoneOk ? lookup.data?.data : undefined;
  const isNew = lookup.error instanceof ApiError && lookup.error.status === 404;

  /*
    Бүртгэлтэй бол ХООСОН талбарыг бөглөнө. Өмнө нь АВТОМАТААР бөглөсөн
    утга (кассчин засаагүй) бол утас солигдоход шинэ хүнийхээр солигдоно —
    А хүний нэр Б хүний утастай үлдэхгүй. Кассчны шивсэн утга хэвээр.
  */
  const autoFilled = useRef({ name: '', registry: '' });
  useEffect(() => {
    const prev = autoFilled.current;
    const next = { name: known?.name ?? '', registry: known?.registryNo ?? '' };
    setName((n) => (n === prev.name ? next.name : n));
    setRegistry((r) => (r === prev.registry ? next.registry : r));
    autoFilled.current = next;
  }, [known]);

  useEffect(() => {
    onChange(
      phoneBad || registryBad || missing
        ? null
        : {
            ...(phoneOk ? { customerPhone: phoneOk } : {}),
            ...(name.trim() ? { customerName: name.trim() } : {}),
            ...(registryOk ? { customerRegistryNo: registryOk } : {}),
            ...(promotion.trim() ? { promotion: promotion.trim() } : {}),
            marketingConsent: consent,
          },
    );
  }, [phoneOk, registryOk, phoneBad, registryBad, missing, name, consent, promotion, onChange]);

  return (
    <Card>
      <CardTitle
        right={
          required ? (
            <span className="text-[11.5px] text-alert-ink">Хувь зээлд утас, регистр заавал</span>
          ) : (
            <span className="text-[11.5px] text-ink-3">Заавал биш</span>
          )
        }
      >
        Худалдан авагч
      </CardTitle>
      <div className="flex flex-col gap-3">
        <Field label="Утас">
          <Input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="99112233"
            inputMode="tel"
            className="num"
          />
        </Field>
        {phoneBad ? <p className="text-[12.5px] text-alert-ink">Утас 8 оронтой байна</p> : null}
        {phoneOk && (known || isNew || !canLookup) ? (
          <p className={`flex items-center gap-2 text-label ${known || !canLookup ? 'text-accent-700' : 'text-ink-2'}`}>
            <span
              aria-hidden
              className={`grid size-3.5 shrink-0 place-items-center rounded-full text-[10px] font-black ${
                known || !canLookup ? 'pop-in bg-accent text-bg' : 'border border-ink-3'
              }`}
            >
              {known || !canLookup ? '✓' : ''}
            </span>
            {known
              ? `Бүртгэлтэй худалдан авагч · ${formatQty(known._count.orders)} худалдан авалт`
              : isNew
                ? 'Шинэ худалдан авагч — баталгаа энэ утсанд бүртгэгдэнэ'
                : 'Баталгааны бүртгэлд холбогдоно'}
          </p>
        ) : null}

        <Field label="Нэр">
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>

        {required ? (
          <>
            <Field label="Регистр">
              <Input
                value={registry}
                onChange={(e) => setRegistry(e.target.value)}
                placeholder="УБ12345678"
                autoCapitalize="characters"
              />
            </Field>
            {registryBad ? (
              <p className="text-[12.5px] text-alert-ink">Регистр буруу байна (жишээ: УБ12345678)</p>
            ) : null}
          </>
        ) : null}

        <PromotionField value={promotion} onChange={setPromotion} />

        <label className="flex items-center gap-2 text-[13px] text-ink-2">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="size-4 accent-accent"
          />
          Маркетингийн зөвшөөрөл өгсөн
        </label>
        <p className="text-[11.5px] text-ink-3">
          Утас оруулбал баталгааны бүртгэл худалдан авагчид холбогдоно.
        </p>
      </div>
    </Card>
  );
}
